import { ComponentClass } from 'react';
import {
  Menu,
  MenuItem,
  Token,
  Typeahead,
  TypeaheadProps,
} from 'react-bootstrap-typeahead';
import styled, { css } from 'styled-components';
import { screenReaderOnlyStyle } from '../../../commonStyles/screenReaderOnly';
import colors, { colorWithOpacity } from '../../../helpers/colors';
import fonts from '../../../helpers/fonts';
import mediaQuery from '../../../helpers/mediaQuery';
import spacing from '../../../helpers/spacing';
import { ButtonLink } from '../../Link';

export type Option = {
  id?: string;
  label: string;
};

// This typing is a bit of a hack, but currently StyledComponents does
// not support preserving generic types, see
// https://github.com/styled-components/styled-components/issues/1803
export const StyledTypeahead = styled(
  Typeahead as ComponentClass<TypeaheadProps<Option>>
)`
  .rbt-input-wrapper {
    width: 100%;
    min-height: ${spacing(7)};
    height: auto;
    padding: ${spacing(0.5)} ${spacing(1)};

    border: 1px solid ${colors.neutral03};
    border-radius: ${({ theme }) => theme.inputRadius};

    align-items: center;
    display: flex;
    flex-wrap: wrap;
    margin-top: -1px;
    overflow: hidden;

    :focus-within {
      border-color: ${colors.core01};
    }
  }

  .rbt-input {
    ${fonts.paragraph};
  }

  .rbt-input-main {
    width: 100%;
    margin-left: ${spacing(1)};
    min-width: 200px;
  }

  .rbt-input-multi {
    height: auto;
    align-items: center;
  }

  .rbt-input-multi.focus {
    border-radius: ${({ theme }) => theme.inputRadius};
    box-shadow: 0 0 0 0.4rem ${colorWithOpacity(colors.core01, 0.6)};
    outline: ${colors.neutral08} solid 2px;
  }

  .rbt-token-remove-button {
    cursor: pointer;
  }
`;

export const StyledToken = styled(Token)`
  ${fonts.paragraph};
  background-color: ${colors.core01};
  border: 0;
  border-radius: ${({ theme }) => theme.buttonRadius};
  color: ${colors.neutral08};
  display: inline-flex;
  line-height: 2rem;
  margin: ${spacing(1)} ${spacing(0.5)};
  padding: 4px 16px 4px 16px;
  height: ${spacing(5)};
  align-items: center;
  justify-content: center;
  user-select: none;

  .visually-hidden {
    display: none;
  }

  .rbt-close {
    color: ${colors.core01};
    background-color: ${colors.neutral08};
    border-radius: 50%;
    border: 0;
    margin-left: ${spacing(1)};
    display: inline-flex;
    align-items: center;
    padding-bottom: 4px;
  }

  .rbt-close:first-child {
    ${fonts.paragraph};
    font-weight: bolder !important;
    display: inline-flex;
    height: ${spacing(2)};
    width: ${spacing(2)};
    justify-content: center;
    text-decoration: none;
  }

  :hover {
    box-shadow: 0 0 0 0.3rem ${colorWithOpacity(colors.core01, 0.6)};
  }

  :focus-visible {
    box-shadow: 0 0 0 0.3rem ${colorWithOpacity(colors.core01, 0.6)};
    outline: ${colors.neutral08} solid 2px;
  }
`;

export const ClearFiltersButton = styled(ButtonLink)`
  margin-left: auto;
  ${fonts.paragraph};
  color: ${colors.core01};
  text-decoration: underline;
  margin-bottom: ${spacing(1)};
`;

export const StyledMenu = styled(Menu)`
  position: static !important;
  transform: none !important;
  scroll-snap-type: y mandatory;
  scroll-padding-top: calc(${spacing(2)});

  background-color: ${colors.neutral08};
  border: 1px solid ${colors.neutral03};
  border-radius: ${({ theme }) => theme.cardRadius} 0 0 ${({ theme }) => theme.cardRadius};
  padding: ${spacing(2)};
  padding-bottom: 0;
  margin: ${spacing(1.5)} 0 ${spacing(1)};
  max-height: 252px !important;
  list-style-type: none;

  > :last-child {
    margin-bottom: ${spacing(2)};
  }

  /* Scrollbar styling for Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${colors.neutral03} ${colors.neutral08};

  /* Scrollbar styling for webkit based browsers */
  @media screen and (-webkit-min-device-pixel-ratio: 0) {
    border-radius: ${({ theme }) => theme.cardRadius};
  }

  ::-webkit-scrollbar {
    width: calc(2 * ${spacing(2)} + 6px);
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: ${colors.neutral04};
    border: ${spacing(2)} ${colors.neutral08} solid;
    border-radius: calc(${spacing(2)} + 6px);
    min-height: calc(${spacing(4)} + 16px);
  }
`;

const baseMenuItemStyles = css`
  ${fonts.paragraph};
  color: ${colors.neutral02};
  padding: ${spacing(1.5)} ${spacing(2)};
  margin-bottom: ${spacing(1)};
  display: block;
  text-decoration: none;
  border-radius: ${({ theme }) => theme.cardRadius};

  ${mediaQuery.tabletPortrait`
    ${fonts.paragraph}
  `}
`;

export const StyledMenuItem = styled(MenuItem)`
  ${baseMenuItemStyles}

  &:focus-visible,
  :hover,
  &.active {
    background-color: ${colorWithOpacity(colors.core01, 0.1)};
    outline: 1px solid ${colorWithOpacity(colors.core01, 0.6)};
  }

  :disabled {
    pointer-events: none;
  }

  scroll-snap-align: start;
`;

export const DisabledMenuItem = styled(MenuItem)`
  ${baseMenuItemStyles}
  pointer-events: none;
`;

export const InputWrapper = styled.div`
  position: relative;
`;

export const HiddenMenuDescription = styled.p`
  ${screenReaderOnlyStyle}
`;
